import { getAccessToken } from './token';

type WsMessage = { topic?: string; event?: string; payload?: unknown };
type Handler = (msg: WsMessage) => void;

// Dev goes through the Vite proxy (same host as the page); prod uses VITE_WS_URL.
function wsUrl(): string {
  if (import.meta.env.VITE_APP_ENV === 'development') {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}/ws`;
  }
  return import.meta.env.VITE_WS_URL as string;
}

/**
 * Minimal realtime client matching the backend handshake:
 *   connect → {action:auth,token} → {event:authenticated} → subscribe topics.
 * Auto-reconnects with backoff and re-subscribes. One socket per app.
 */
class RealtimeClient {
  private ws: WebSocket | null = null;
  private topics = new Set<string>();
  private handlers = new Set<Handler>();
  private authed = false;
  private backoff = 1000;
  private closedByUs = false;

  connect(): void {
    if (this.ws || !getAccessToken()) return;
    this.closedByUs = false;
    const ws = new WebSocket(wsUrl());
    this.ws = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'auth', token: getAccessToken() }));
    };
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data) as WsMessage & { event?: string };
      if (msg.event === 'authenticated') {
        this.authed = true;
        this.backoff = 1000;
        this.topics.forEach((t) => ws.send(JSON.stringify({ action: 'subscribe', topic: t })));
        return;
      }
      if (msg.event === 'ping') {
        ws.send(JSON.stringify({ action: 'pong' }));
        return;
      }
      this.handlers.forEach((h) => h(msg));
    };
    ws.onclose = () => {
      this.ws = null;
      this.authed = false;
      if (!this.closedByUs && getAccessToken()) {
        setTimeout(() => this.connect(), this.backoff);
        this.backoff = Math.min(this.backoff * 2, 15_000);
      }
    };
    ws.onerror = () => ws.close();
  }

  subscribe(topic: string): () => void {
    this.topics.add(topic);
    if (this.authed && this.ws) this.ws.send(JSON.stringify({ action: 'subscribe', topic }));
    else this.connect();
    return () => {
      this.topics.delete(topic);
      if (this.authed && this.ws) this.ws.send(JSON.stringify({ action: 'unsubscribe', topic }));
    };
  }

  on(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  disconnect(): void {
    this.closedByUs = true;
    this.topics.clear();
    this.ws?.close();
    this.ws = null;
  }
}

export const realtime = new RealtimeClient();
export type { WsMessage };
